/**
 * PublicationUpdateModal — met à jour une publication active (textes
 * draft + diff add/remove mockups, S6 PR2-C).
 *
 * Réutilisable cross-plateforme : le caller passe `platformLabel` +
 * `platform`. Le composant ne sait rien d'Etsy en propre.
 *
 * Forme :
 *   1. Select draft approuvé (filtré par platform côté caller via
 *      `availableDrafts` prop).
 *   2. Bouton "Modifier les mockups" qui ouvre `<MockupTemplatePicker>`
 *      en mode update (currentMockupTemplateIds pré-cochés, badge
 *      "Actuel"). Si non touché : submit envoie `mockup_template_ids:
 *      null` au backend (= garde la liste actuelle, juste PATCH textes).
 *   3. Submit → onConfirm({draft_id, mockup_template_ids|null}).
 */

import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import Select from '@/components/ui/Select'
import MockupTemplatePicker from '@/components/etsy/MockupTemplatePicker'
import type { components } from '@/types/api-generated'

import './PublicationUpdateModal.css'

type DraftResponse = components['schemas']['DraftResponse']
type PlatformPublicationUpdateRequest = components['schemas']['PlatformPublicationUpdateRequest']

const schema = z.object({
  draft_id: z.number().int().positive(),
  mockup_template_ids: z.array(z.number().int().positive()).min(1).max(8).nullable(),
})

type FormValues = z.infer<typeof schema>

interface PublicationUpdateModalProps {
  isOpen: boolean
  onClose: () => void
  workId: number
  workTitle: string
  /** Format slug de la variante publiée — alimente le matching MockupTemplatePicker. */
  variantFormatSlug?: string | null
  /** Drafts approuvés disponibles pour cette plateforme (filtrés côté caller). */
  availableDrafts: DraftResponse[]
  /** IDs de templates actuellement attachés à la publication active. */
  currentMockupTemplateIds: number[]
  platformLabel: string
  /** Caller exécute la mutation API + ferme + toast. */
  onConfirm: (payload: PlatformPublicationUpdateRequest) => void | Promise<void>
  isSubmitting?: boolean
}

export default function PublicationUpdateModal({
  isOpen,
  onClose,
  workId,
  workTitle,
  variantFormatSlug,
  availableDrafts,
  currentMockupTemplateIds,
  platformLabel,
  onConfirm,
  isSubmitting = false,
}: PublicationUpdateModalProps) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const defaultDraftId = availableDrafts[0]?.id ?? 0
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      draft_id: defaultDraftId,
      // null = "garde la liste actuelle". L'utilisatrice cliquera sur
      // "Modifier les mockups" pour passer en mode diff.
      mockup_template_ids: null,
    },
  })

  const watchedMockups = useWatch({
    control: form.control,
    name: 'mockup_template_ids',
  })
  const mockupsChanged = watchedMockups !== null

  const handleSubmit = form.handleSubmit(async (values) => {
    await onConfirm({
      draft_id: values.draft_id,
      mockup_template_ids: values.mockup_template_ids,
    })
  })

  const handlePickerConfirm = (mockupTemplateIds: number[]) => {
    form.setValue('mockup_template_ids', mockupTemplateIds, {
      shouldDirty: true,
      shouldValidate: true,
    })
    setPickerOpen(false)
  }

  const resetMockups = () => {
    form.setValue('mockup_template_ids', null, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  return (
    <>
      <Modal
        isOpen={isOpen && !pickerOpen}
        onClose={() => {
          if (!isSubmitting) {
            form.reset()
            onClose()
          }
        }}
        kicker={`${platformLabel} · Mettre à jour`}
        title={`Mise à jour « ${workTitle} »`}
        size="form"
      >
        <FormProvider {...form}>
          <form onSubmit={handleSubmit}>
            <Modal.Body>
              <Field label="Draft à appliquer" required>
                {(controlProps) => (
                  <Controller
                    name="draft_id"
                    control={form.control}
                    render={({ field }) => (
                      <Select
                        {...controlProps}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        onBlur={field.onBlur}
                      >
                        {availableDrafts.length === 0 ? (
                          <option value="" disabled>
                            Aucun draft approuvé pour {platformLabel}
                          </option>
                        ) : (
                          availableDrafts.map((draft) => {
                            const title = draft.current_version?.title ?? `Draft #${draft.id}`
                            const versionNumber = draft.versions_count ?? 1
                            return (
                              <option key={draft.id} value={draft.id}>
                                {title} (v{versionNumber})
                              </option>
                            )
                          })
                        )}
                      </Select>
                    )}
                  />
                )}
              </Field>

              <Field
                label="Mockups attachés"
                helper={
                  mockupsChanged
                    ? `Nouvelle liste : ${watchedMockups?.length ?? 0} mockup(s).`
                    : `${currentMockupTemplateIds.length} mockup(s) actuellement attachés. Conservés tels quels si tu ne touches pas.`
                }
              >
                {() => (
                  <div className="publication-update-modal-mockups">
                    <Button type="button" variant="neutral" onClick={() => setPickerOpen(true)}>
                      {mockupsChanged ? 'Modifier la nouvelle liste' : 'Modifier les mockups'}
                    </Button>
                    {mockupsChanged ? (
                      <Button type="button" variant="quiet" onClick={resetMockups}>
                        Annuler le changement (garder l'actuel)
                      </Button>
                    ) : null}
                  </div>
                )}
              </Field>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="neutral" onClick={onClose} disabled={isSubmitting}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || availableDrafts.length === 0}
              >
                {isSubmitting ? 'Mise à jour…' : 'Mettre à jour'}
              </Button>
            </Modal.Footer>
          </form>
        </FormProvider>
      </Modal>

      <MockupTemplatePicker
        workId={workId}
        formatSlug={variantFormatSlug ?? null}
        workTitle={workTitle}
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        currentMockupTemplateIds={watchedMockups ?? currentMockupTemplateIds}
        confirmLabel="Valider la sélection"
        submittingLabel="Validation…"
        kicker={`${platformLabel} · Mise à jour mockups`}
        title={`Mockups pour « ${workTitle} »`}
      />
    </>
  )
}
